import { useAtomValue } from "jotai"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useAgreeEchoesConsent } from "@/api/generated/endpoints/api"
import { getErrorMessage } from "@/api/instance"
import logo from "@/assets/logo.png"
import { Button } from "@/components/ui/button/button"
import { Checkbox } from "@/components/ui/checkbox/checkbox"
import { isEchoesConsentRequiredAtom, isEchoesOwnerAtom, useSessionRefetch } from "@/store/session"

/**
 * Echoes連携の二層同意画面（要件§5.1 / 画像5-C）
 *
 * ①Echoesとのデータ連携への同意 → ②AgentCall利用規約への同意 の2段階を取得し、
 * 両方同意でダッシュボードへ遷移する。同意状態はBE側で echoesLinkedAt に記録される。
 */
export function EchoesConsentPage() {
  const navigate = useNavigate()
  const refetchSession = useSessionRefetch()
  const isEchoesOwner = useAtomValue(isEchoesOwnerAtom)
  const isConsentRequired = useAtomValue(isEchoesConsentRequiredAtom)

  const [step, setStep] = useState<1 | 2>(1)
  const [agreeDataLink, setAgreeDataLink] = useState(false)
  const [agreeTerms, setAgreeTerms] = useState(false)

  const { mutateAsync: agreeConsent, isPending } = useAgreeEchoesConsent({
    mutation: {
      onError: (error) => {
        toast.error("同意処理に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  useEffect(() => {
    if (!isEchoesOwner || !isConsentRequired) {
      navigate("/", { replace: true })
    }
  }, [isEchoesOwner, isConsentRequired, navigate])

  const handleSubmit = async () => {
    try {
      await agreeConsent({ data: { agreeDataLink, agreeTerms } })
      toast.success("同意が完了しました")
      await refetchSession()
      navigate("/", { replace: true })
    } catch (error) {
      console.error("Echoes consent error:", error)
    }
  }

  return (
    <div className="flex flex-col items-center">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-4">
            <img src={logo} alt="AGENTCALL エージェントコール" className="w-2/3 h-auto" />
          </div>
          <p className="text-sm text-slate-500 leading-relaxed">
            ご利用を開始する前に、以下の内容にご同意ください。
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 mb-8">
          <span className={`h-2 w-2 rounded-full ${step === 1 ? "bg-primary" : "bg-slate-300"}`} />
          <span className={`h-2 w-2 rounded-full ${step === 2 ? "bg-primary" : "bg-slate-300"}`} />
        </div>

        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold text-slate-700 mb-2">Echoesとのデータ連携</h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                Echoesに登録された物件・部屋・賃借人の情報をAgentCallに取り込み、
                電話代行や案件管理に利用します。情報の編集はEchoes側で行います。
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer rounded-md border border-slate-200 bg-white p-4">
              <Checkbox
                checked={agreeDataLink}
                onCheckedChange={(checked) => setAgreeDataLink(checked === true)}
                className="mt-0.5"
              />
              <span className="text-sm font-medium text-slate-700">
                Echoesとのデータ連携に同意します
              </span>
            </label>

            <Button
              type="button"
              className="w-full h-11 font-bold"
              disabled={!agreeDataLink}
              onClick={() => setStep(2)}
            >
              次へ
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold text-slate-700 mb-2">AgentCall利用規約</h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                AgentCallの
                {/* TODO: 利用規約リンクのURLを routePaths 等に集約する */}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-bold underline"
                >
                  利用規約
                </a>
                をご確認のうえ、同意してください。
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer rounded-md border border-slate-200 bg-white p-4">
              <Checkbox
                checked={agreeTerms}
                onCheckedChange={(checked) => setAgreeTerms(checked === true)}
                className="mt-0.5"
              />
              <span className="text-sm font-medium text-slate-700">
                AgentCall利用規約に同意します
              </span>
            </label>

            <div className="space-y-3">
              <Button
                type="button"
                className="w-full h-11 font-bold"
                disabled={!agreeTerms || !agreeDataLink}
                loading={isPending}
                onClick={handleSubmit}
              >
                同意して始める
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 font-bold"
                disabled={isPending}
                onClick={() => setStep(1)}
              >
                戻る
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
